import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import api from '../services/api';

const BulkImport = () => {
  const [file, setFile] = useState(null);
  const [dataType, setDataType] = useState('tenants');
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState('');
  const [step, setStep] = useState('upload'); // upload, preview, confirm, done
  const [apiKey, setApiKey] = useState('');
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState(new Set());
  const [selectAll, setSelectAll] = useState(true);
  const fileInputRef = useRef(null);

  // Check if DeepSeek API key is configured
  useEffect(() => {
    const storedKey = localStorage.getItem('deepseek_api_key');
    if (storedKey) {
      setApiKey(storedKey);
    }
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError('');
      setStep('upload');
      setParsedData(null);
      setImportResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('dataType', dataType);

      const response = await api.post('/bulk-import/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data?.success) {
        setParsedData(response.data.data);
        setStep('preview');
        // Select all records by default
        const allIndices = new Set(
          response.data.data.records.map((_, i) => i)
        );
        setSelectedRecords(allIndices);
        setSelectAll(true);
      } else {
        setError(response.data?.error || 'Failed to parse file');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || err.response?.data?.details || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleImport = async () => {
    if (!parsedData?.records?.length) {
      setError('No records to import');
      return;
    }

    // Get selected records
    const recordsToImport = parsedData.records.filter((_, i) => selectedRecords.has(i));
    if (recordsToImport.length === 0) {
      setError('Please select at least one record to import');
      return;
    }

    setImporting(true);
    setError('');

    try {
      const endpoint = dataType === 'tenants' ? '/bulk-import/import-tenants' : '/bulk-import/import-payments';
      const response = await api.post(endpoint, { records: recordsToImport });

      if (response.data?.success) {
        setImportResult(response.data.data);
        setStep('done');
      } else {
        setError(response.data?.error || 'Import failed');
      }
    } catch (err) {
      console.error('Import error:', err);
      setError(err.response?.data?.error || err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    
    try {
      const key = apiKey || undefined;
      const response = await api.post('/bulk-import/test-connection', { apiKey: key });
      
      if (response.data?.success) {
        setConnectionStatus({ type: 'success', message: response.data.message });
        if (apiKey) {
          localStorage.setItem('deepseek_api_key', apiKey);
        }
      } else {
        setConnectionStatus({ type: 'error', message: response.data?.error || 'Connection failed' });
      }
    } catch (err) {
      setConnectionStatus({
        type: 'error',
        message: err.response?.data?.error || err.message || 'Connection failed'
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const toggleRecord = (index) => {
    const newSelected = new Set(selectedRecords);
    if (newSelected.has(index)) {
      newSelected.delete(index);
      setSelectAll(false);
    } else {
      newSelected.add(index);
      if (newSelected.size === parsedData?.records?.length) {
        setSelectAll(true);
      }
    }
    setSelectedRecords(newSelected);
  };

  const toggleAll = () => {
    if (selectAll) {
      setSelectedRecords(new Set());
      setSelectAll(false);
    } else {
      const allIndices = new Set(parsedData.records.map((_, i) => i));
      setSelectedRecords(allIndices);
      setSelectAll(true);
    }
  };

  const resetAll = () => {
    setFile(null);
    setParsedData(null);
    setImportResult(null);
    setError('');
    setStep('upload');
    setSelectedRecords(new Set());
    setSelectAll(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return `KES ${parseFloat(amount).toLocaleString()}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-KE', {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch { return dateStr; }
  };

  // Upload Step
  const renderUploadStep = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">About Bulk Import</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>Upload a file (PDF, Excel, or CSV) and AI will automatically extract records for bulk import.</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li><strong>Tenants</strong>: Creates tenant accounts and lease agreements from the file data</li>
                <li><strong>Payments</strong>: Records payments for existing tenants (import tenants first)</li>
                <li>Supported formats: <code>.pdf</code>, <code>.xlsx</code>, <code>.csv</code></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Data Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Import Type</label>
        <div className="grid grid-cols-2 gap-4">
          <div className="relative">
            <button
              onClick={() => setDataType('tenants')}
              className={`w-full p-4 rounded-lg border-2 text-center transition-all ${
                dataType === 'tenants'
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <svg className="h-8 w-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <div className="font-medium">Tenants + Leases</div>
              <div className="text-xs mt-1">Create tenants, leases, and agreements</div>
            </button>
            {/* Template download link - opens in new tab with auth token */}
            <button
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  const response = await api.get('/bulk-import/template/tenants', {
                    responseType: 'blob',
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', 'tenant-import-template.xlsx');
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                } catch (err) {
                  console.error('Download failed:', err);
                }
              }}
              className="mt-2 w-full flex items-center justify-center text-xs text-indigo-600 hover:text-indigo-800 font-medium"
            >
              <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Template (.xlsx)
            </button>
          </div>
          <div className="relative">
            <button
              onClick={() => setDataType('payments')}
              className={`w-full p-4 rounded-lg border-2 text-center transition-all ${
                dataType === 'payments'
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <svg className="h-8 w-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="font-medium">Payments</div>
              <div className="text-xs mt-1">Record payments for existing tenants</div>
            </button>
            {/* Template download link */}
            <button
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  const response = await api.get('/bulk-import/template/payments', {
                    responseType: 'blob',
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', 'payment-import-template.xlsx');
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                } catch (err) {
                  console.error('Download failed:', err);
                }
              }}
              className="mt-2 w-full flex items-center justify-center text-xs text-indigo-600 hover:text-indigo-800 font-medium"
            >
              <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Template (.xlsx)
            </button>
          </div>
        </div>
      </div>

      {/* File Upload */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors">
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.xlsx,.xls,.csv"
          onChange={handleFileChange}
          className="hidden"
          id="file-upload"
        />
        <label htmlFor="file-upload" className="cursor-pointer">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p className="mt-2 text-sm text-gray-600">
            <span className="font-medium text-indigo-600">Click to upload</span> or drag and drop
          </p>
          <p className="text-xs text-gray-500 mt-1">PDF, Excel (.xlsx), or CSV up to 10MB</p>
        </label>
        {file && (
          <div className="mt-4 flex items-center justify-center space-x-2">
            <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-gray-700">{file.name}</span>
            <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
          </div>
        )}
      </div>

      {/* DeepSeek API Key */}
      <div>
        <button
          onClick={() => setShowApiKeyInput(!showApiKeyInput)}
          className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center"
        >
          <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
          {showApiKeyInput ? 'Hide API Key' : 'Configure DeepSeek API Key'}
        </button>
        {showApiKeyInput && (
          <div className="mt-2 flex space-x-2">
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Enter your DeepSeek API key..."
              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
            />
            <button
              onClick={handleTestConnection}
              disabled={!apiKey || testingConnection}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              {testingConnection ? (
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : 'Test'}
            </button>
          </div>
        )}
        {connectionStatus && (
          <div className={`mt-2 text-sm ${connectionStatus.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
            {connectionStatus.message}
          </div>
        )}
      </div>

      {/* Upload Button */}
      <button
        onClick={handleUpload}
        disabled={!file || uploading}
        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {uploading ? (
          <>
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Parsing file with AI...
          </>
        ) : (
          'Upload & Parse with AI'
        )}
      </button>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="ml-3 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}
    </div>
  );

  // Preview Step
  const renderPreviewStep = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            Preview {dataType === 'tenants' ? 'Tenant' : 'Payment'} Records
          </h3>
          <p className="text-sm text-gray-500">
            {parsedData?.count || 0} records extracted from file. Review and select records to import.
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={resetAll}
            className="px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={importing || selectedRecords.size === 0}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
          >
            {importing ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Importing...
              </>
            ) : (
              `Import ${selectedRecords.size} Records`
            )}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-green-700">{parsedData?.records?.length || 0}</div>
          <div className="text-xs text-green-600">Total Records</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-blue-700">{selectedRecords.size}</div>
          <div className="text-xs text-blue-600">Selected</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-gray-700">{(parsedData?.records?.length || 0) - selectedRecords.size}</div>
          <div className="text-xs text-gray-600">Skipped</div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Records Table */}
      <div className="bg-white shadow overflow-hidden rounded-lg">
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={toggleAll}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                </th>
                {dataType === 'tenants' ? (
                  <>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Property/Unit</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rent</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                  </>
                ) : (
                  <>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {parsedData?.records?.map((record, index) => (
                <tr key={index} className={selectedRecords.has(index) ? 'bg-indigo-50' : 'hover:bg-gray-50'}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selectedRecords.has(index)}
                      onChange={() => toggleRecord(index)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </td>
                  {dataType === 'tenants' ? (
                    <>
                      <td className="px-3 py-2 text-sm text-gray-900">
                        {record.first_name || ''} {record.last_name || ''}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-500">{record.email || '-'}</td>
                      <td className="px-3 py-2 text-sm text-gray-500">
                        {record.asset_name || ''}{record.unit_number ? ` / ${record.unit_number}` : ''}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900">{formatCurrency(record.rent_amount)}</td>
                      <td className="px-3 py-2 text-sm text-gray-500">
                        {formatDate(record.start_date)} - {formatDate(record.end_date)}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2 text-sm text-gray-900">{record.tenant_name || record.tenant_email || 'Unknown'}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{formatCurrency(record.amount)}</td>
                      <td className="px-3 py-2 text-sm text-gray-500">{record.payment_method || '-'}</td>
                      <td className="px-3 py-2 text-sm text-gray-500">{formatDate(record.transaction_date)}</td>
                      <td className="px-3 py-2 text-sm text-gray-500 truncate max-w-[150px]">{record.notes || '-'}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Available Assets Info */}
      {parsedData?.availableAssets?.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-500">
            Available properties: {parsedData.availableAssets.map(a => a.name).join(', ')}
          </p>
        </div>
      )}
    </div>
  );

  // Done Step
  const renderDoneStep = () => (
    <div className="space-y-6">
      <div className={`rounded-lg p-6 text-center ${
        importResult?.failed > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'
      }`}>
        {importResult?.failed > 0 ? (
          <svg className="h-12 w-12 mx-auto text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        ) : (
          <svg className="h-12 w-12 mx-auto text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )}
        <h3 className="mt-3 text-lg font-medium text-gray-900">
          Import Complete
        </h3>
        <div className="mt-2 text-sm text-gray-600">
          <p>Successfully imported {importResult?.successful || 0} of {importResult?.total || 0} records.</p>
          {importResult?.failed > 0 && (
            <p className="text-yellow-700 mt-1">{importResult.failed} records failed. Check errors below.</p>
          )}
        </div>
      </div>

      {/* Success Summary */}
      {importResult?.created?.length > 0 && (
        <div className="bg-white shadow overflow-hidden rounded-lg">
          <div className="px-4 py-3 bg-gray-50 border-b">
            <h4 className="text-sm font-medium text-gray-700">Successfully Created</h4>
          </div>
          <div className="overflow-x-auto max-h-60 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {dataType === 'tenants' ? (
                    <>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Lease ID</th>
                    </>
                  ) : (
                    <>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payment ID</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {importResult.created.map((item, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-900">{item.tenant_name || item.email}</td>
                    <td className="px-3 py-2 text-sm text-gray-500">{item.email || formatCurrency(item.amount)}</td>
                    <td className="px-3 py-2 text-sm text-gray-500">{item.lease_id || item.payment_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Error Details */}
      {importResult?.errors?.length > 0 && (
        <div className="bg-white shadow overflow-hidden rounded-lg border border-red-200">
          <div className="px-4 py-3 bg-red-50 border-b border-red-200">
            <h4 className="text-sm font-medium text-red-700">Failed Records ({importResult.errors.length})</h4>
          </div>
          <div className="divide-y divide-gray-200 max-h-60 overflow-y-auto">
            {importResult.errors.map((err, i) => (
              <div key={i} className="px-4 py-3">
                <p className="text-sm font-medium text-gray-900">Record #{err.index + 1}</p>
                <p className="text-sm text-red-600">{err.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-center space-x-4">
        <button
          onClick={resetAll}
          className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Import Another File
        </button>
        <button
          onClick={() => window.location.href = dataType === 'tenants' ? '/leases' : '/payments'}
          className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
        >
          View {dataType === 'tenants' ? 'Leases' : 'Payments'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Bulk Import</h1>
          <p className="mt-1 text-sm text-gray-500">
            Import tenants/leases or payments in bulk using AI-powered document parsing.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {['upload', 'preview', 'confirm', 'done'].map((s, i) => {
              const stepNames = ['Upload File', 'Preview Data', 'Confirm Import', 'Complete'];
              const isActive = step === s;
              const isDone = ['done'].includes(step) || (step === 'confirm' && ['preview', 'confirm', 'done'].includes(s)) || (step === 'preview' && ['upload', 'preview'].includes(s));
              return (
                <div key={s} className="flex items-center">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                    isActive ? 'bg-indigo-600 text-white' :
                    isDone ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {isDone && step !== s ? '✓' : i + 1}
                  </div>
                  <div className={`ml-2 text-sm ${isActive ? 'text-indigo-600 font-medium' : 'text-gray-500'}`}>
                    {stepNames[i]}
                  </div>
                  {i < 3 && <div className="mx-4 w-16 h-0.5 bg-gray-200" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white shadow-lg rounded-lg p-6">
          {step === 'upload' && renderUploadStep()}
          {step === 'preview' && renderPreviewStep()}
          {step === 'done' && renderDoneStep()}
        </div>
      </div>
    </div>
  );
};

export default BulkImport;
